import CrashCourseCard from 'components/CrashCourseCard';
import type { EventData } from 'services/Gtm/entities';
import type { CrashCourseArticle } from 'interfaces/content/articles/Articles';
import { Microsites } from 'constants/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import cx from 'classnames';
import { Container, NoResults } from './CrashCourseMasonryStyles';

interface Props {
  cards: CrashCourseArticle[];
  columnCount: number;
  className: string;
}

// Define a more specific type for card placement
interface CardPlacement {
  gridRowEnd: number | undefined;
  gridRowStart: number | undefined;
  gridColumn: number | undefined;
}

const indexOfMinValue = (items: number[]): number =>
  items.reduce(
    (minIndex, currentValue, currentIndex, array) => (currentValue < array[minIndex] ? currentIndex : minIndex),
    0,
  );

const Masonry = ({ cards, columnCount, className }: Props) => {
  const [innerWidth, setInnerWidth] = useState(320);
  const refsArray = useRef<(HTMLDivElement | null)[]>([]);
  const [cardsPlacements, setCardsPlacements] = useState<CardPlacement[]>(
    Array(cards.length).fill({ gridColumn: null, gridRowEnd: null, gridRowStart: null }),
  );
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Throttled resize handler
  useEffect(() => {
    const handleResize = throttle(() => {
      setInnerWidth(window.innerWidth);
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // Calculate card placements on layout changes
  useEffect(() => {
    const columnHeights: number[] = Array<number>(columnCount).fill(0);
    const placements: CardPlacement[] = [];
    const gapSize = 40;

    refsArray.current.forEach((cardRef, index) => {
      if (cardRef) {
        const currentCardHeight = (cardRef.children[0] as HTMLDivElement).offsetHeight;
        const shortestColumn = indexOfMinValue(columnHeights);
        const currentColumnHeight = columnHeights[shortestColumn];

        placements[index] = {
          gridColumn: shortestColumn + 1,
          gridRowEnd: currentColumnHeight + currentCardHeight,
          gridRowStart: currentColumnHeight || 1,
        };

        columnHeights[shortestColumn] += currentCardHeight + gapSize;
      }
    });

    setCardsPlacements(placements);
  }, [innerWidth, cards, columnCount]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    // Initialize observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0', 10);
            setVisibleCards((prevVisible) => [...prevVisible, index]);
          }
        });
      },
      { threshold: 0.1 },
    );

    refsArray.current.forEach((card) => {
      if (card && observerRef.current) {
        observerRef.current.observe(card);
      }
    });

    return () => observerRef.current?.disconnect();
  }, [cards.length]);

  const renderCards = useCallback(() => {
    const trackingData = {
      eventCategory: 'navigation',
      platformCD: Microsites.Crypto,
    } as EventData;

    return cards.map((article, idx) => (
      <div
        key={article.title}
        data-index={idx}
        style={{
          ...cardsPlacements[idx],
          visibility: visibleCards.includes(idx) ? 'visible' : 'hidden',
        }}
        ref={(el) => {
          refsArray.current[idx] = el;
        }}
      >
        <CrashCourseCard
          className='card'
          title={article.title}
          complexity={article.complexity}
          image={article.image}
          imageAlt={article.imageAlt}
          description={article.description}
          href={article.courseLink}
          trackingData={trackingData}
          priority={idx < 3 && article.image !== '' ? true : undefined}
        />
      </div>
    ));
  }, [cards, cardsPlacements, visibleCards]);

  return (
    <Container
      className={cx(className, { 'no-results': cards.length === 0 })}
      data-cy='cardsContainer'
    >
      {cards.length > 0 ? (
        renderCards()
      ) : (
        <NoResults data-cy='crashCourseNoResults'>
          <strong>No results found. </strong>
          Please search for a different term.
        </NoResults>
      )}
    </Container>
  );
};

export default Masonry;
